import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { T } from "@/components/T";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Sun, Grid3x3, Hourglass, CalendarDays, CalendarClock, CircleDot, Compass, Eye, MapPin, Navigation, Loader2, X, Pencil, Plus, BookOpen, ChevronDown, Clock, RotateCcw, Bell, ChevronLeft, ChevronRight, Menu, MoreHorizontal, Languages, Moon, Sparkles, UserPlus, Trash2 } from "lucide-react";
import SectionErrorBoundary from "../components/SectionErrorBoundary";
import { premiumPanchang, premiumChoghadiya, premiumTara, premiumChart, premiumDasha, premiumGetBirth, premiumSaveBirth, premiumGetProfiles, premiumSaveProfiles, premiumStatus, fetchCalendar } from "@/lib/api";
import { toast } from "sonner";
import PremiumPanchang from "@/components/PremiumPanchang";
import ChartBlock from "@/components/ChartBlock";
import AdvancedDasha from "@/components/AdvancedDasha";
import JapaMala from "@/components/JapaMala";
import ThemeToggle from "@/components/ThemeToggle";
import PremiumPlaceInput from "@/components/PremiumPlaceInput";
import { getSavedLocation, setSavedLocation } from "@/lib/cities";
import { PANCHANG_GLOSSARY } from "@/lib/panchangGlossary";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { makePanchangT } from "@/lib/panchangTamil";

// Premium-shell UI labels. Only the static chrome (tabs, headings, sheet labels)
// lives here — astro values always come verbatim from the engine. Falls back to
// English for any missing key / language.
const PLUX_I18N = {
  en: { today: "Today", charts: "Birth", practice: "Practice", prashna: "Prashna", muhurta: "Muhurta", kundali: "Kundali", dasha: "Dasha", birthPanchangam: "Janma Panchangam", japa: "Japa", more: "More", translate: "Translate", language: "Language", comingSoon: "Coming soon" },
  hi: { today: "आज", charts: "जन्म", practice: "साधना", prashna: "प्रश्न", muhurta: "मुहूर्त", kundali: "कुंडली", dasha: "दशा", birthPanchangam: "जन्म पंचांगम्", japa: "जप", more: "और", translate: "अनुवाद", language: "भाषा", comingSoon: "जल्द आ रहा है" },
  ta: { today: "இன்று", charts: "பிறப்பு", practice: "சாதனை", prashna: "பிரஸ்னம்", muhurta: "முகூர்த்தம்", kundali: "ஜாதகம்", dasha: "தசை", birthPanchangam: "ஜன்ம பஞ்சாங்கம்", japa: "ஜபம்", more: "மேலும்", language: "மொழி", translate: "மொழி", comingSoon: "விரைவில்" },
};
const LANG_PILLS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
];

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LOC = { name: "New Delhi", lat: 28.6139, lon: 77.209, tz: "Asia/Kolkata" };
const deviceTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"; } catch { return "Asia/Kolkata"; } };
// Reuse the location the user already picked on the free Panchangam (shared via
// localStorage) so free + premium stay in sync. tz here is the IANA id.
const initLoc = () => {
  const s = getSavedLocation();
  return s && s.lat != null ? { name: s.name, lat: s.lat, lon: s.lon, tz: s.tz_id || deviceTz() } : DEFAULT_LOC;
};

function nowParts(tz) {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).formatToParts(new Date());
  const g = (t) => parseInt(p.find((x) => x.type === t).value, 10);
  return { year: g("year"), month: g("month"), day: g("day"), hour: g("hour") % 24, min: g("minute"), minute: g("minute"), sec: g("second") };
}
const atNow = (loc) => ({ ...nowParts(loc.tz), lat: loc.lat, lon: loc.lon, tz: loc.tz });
// Current calendar date IN the viewer's location timezone (NOT UTC) so the
// date-chip, greeting and the engine fetch all agree on "today".
const isoInTz = (tz) => { const p = nowParts(tz); return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`; };

// Header date-strip helpers — always render "11 Oct 2026", never 11/10/2026.
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtStrip = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" });
};
const shiftISO = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};
const shortLoc = (name) => (name || "").split(",")[0].trim() || "Location";

// "HH:MM" → seconds since midnight (for the Sun/Moon arc's selected moment).
const timeToSec = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 3600 + (m || 0) * 60;
};

// Date / time / location selector for the Daily tab. The chosen moment is sent
// verbatim to the PythonAnywhere engine; location comes from the Google Places
// sheet (opened via onLocation). Theme-aware so it reads in light and dark.
function DailyWhenBar({ when, locName, onDate, onTime, onReset, onLocation }) {
  const today = new Date().toISOString().slice(0, 10);
  const nowT = new Date().toTimeString().slice(0, 5);
  const fieldCls = "inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5";
  const inputCls = "bg-transparent py-2 text-sm text-white [color-scheme:dark] focus:outline-none";
  return (
    <div data-testid="daily-when-bar" className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-300/70"><T>Showing for</T></span>
      <span className={fieldCls}>
        <CalendarDays className="h-4 w-4 shrink-0 text-amber-400" />
        <input type="date" data-testid="daily-date" value={when?.date || today} max="2099-12-31" onChange={(e) => e.target.value && onDate(e.target.value)} className={inputCls} />
      </span>
      <span className={fieldCls}>
        <Clock className="h-4 w-4 shrink-0 text-amber-400" />
        <input type="time" data-testid="daily-time" value={when?.time || nowT} onChange={(e) => e.target.value && onTime(e.target.value)} className={inputCls} />
      </span>
      <button type="button" data-testid="daily-location-btn" onClick={onLocation}
        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-sm text-white/80 transition-colors hover:border-amber-300/50">
        <MapPin className="h-4 w-4 text-amber-400" /> <span className="max-w-[9rem] truncate">{locName}</span>
      </button>
      {when && <button type="button" data-testid="daily-reset" onClick={onReset} className="ml-auto inline-flex items-center gap-1 rounded-full border border-amber-400/40 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-400/10"><RotateCcw className="h-3.5 w-3.5" /> <T>Now</T></button>}
    </div>
  );
}
function birthPayload(b, extra = {}) {
  const [y, m, d] = String(b.dob).split("-").map(Number);
  const [hh, mm] = String(b.tob).split(":").map(Number);
  return { year: y, month: m, day: d, hour: hh || 0, min: mm || 0, minute: mm || 0, sec: 0, lat: b.lat ?? DEFAULT_LOC.lat, lon: b.lon ?? DEFAULT_LOC.lon, tz: b.tz_name || "Asia/Kolkata", ...extra };
}

const TABS = [
  { k: "daily", labelKey: "today", Icon: Sun },
  { k: "chart", labelKey: "charts", Icon: Grid3x3, group: ["chart", "dasha", "birthpan"] },
  { k: "japa", labelKey: "practice", Icon: Eye, iconClass: "rotate-90" },
  { k: "prashna", labelKey: "prashna", Icon: Compass },
  { k: "muhurta", labelKey: "muhurta", Icon: CalendarClock },
];

// Segmented switch used inside Charts (Kundali/Birth Panchang) and Practice (Japa/Prashna).
function Segmented({ value, onChange, options }) {
  return (
    <div className="mb-5 inline-flex rounded-full border border-white/12 bg-white/5 p-1" data-testid="premium-segmented">
      {options.map((o) => (
        <button key={o.k} type="button" onClick={() => onChange(o.k)} data-testid={`seg-${o.k}`}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${value === o.k ? "bg-amber-400 text-black" : "text-white/65 hover:text-white"}`}>{o.label}</button>
      ))}
    </div>
  );
}

function MoreMenu() {
  const Item = ({ to, label }) => (
    <Link to={to} data-testid={`more-${to.replace(/\W+/g, "-")}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white/90 transition-colors hover:border-white/25">
      <span>{label}</span><ChevronRight className="h-4 w-4 text-white/40" />
    </Link>
  );
  return (
    <div data-testid="more-tab" className="space-y-3">
      <p className="mb-2 text-xs uppercase tracking-wide text-white/40"><T>Settings & more</T></p>
      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <span className="text-sm text-white/90"><T>Appearance (Light / Dark)</T></span>
        <ThemeToggle className="plux-iconbtn !h-[34px] !w-[34px]" />
      </div>
      <Item to="/glossary" label="Glossary" />
      <Item to="/account" label="Account & profile" />
      <Item to="/support" label="Support Dharmartha" />
      <Item to="/" label="Back to Dharmartha home" />
    </div>
  );
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "1963-08-07" -> "7 Aug 1963" (display-only reformat; never changes the value)
const prettyDob = (dob) => {
  if (!dob) return "";
  const [y, m, d] = String(dob).split("-").map(Number);
  if (!y || !m || !d) return dob;
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
};
const initialOf = (name) => (name || "").trim().charAt(0).toUpperCase() || "•";

const Spin = () => <div className="flex items-center gap-2 py-16 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /> <T>Loading…</T></div>;
function OmLoader({ msg = "Calculating cosmic time…" }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center" data-testid="om-loader">
      <span className="om-pulse font-deva text-5xl leading-none text-amber-300/90">ॐ</span>
      <p className="text-sm text-white/55">{msg}</p>
    </div>
  );
}
const Note = ({ children }) => <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">{children}</div>;

function PremiumGlossary() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = PANCHANG_GLOSSARY.filter((g) => {
    const t = q.trim().toLowerCase();
    return !t || (g.transliteration || "").toLowerCase().includes(t) || (g.devanagari || "").includes(q.trim()) || (g.meaning || "").toLowerCase().includes(t);
  });
  return (
    <section data-testid="premium-glossary" className="mt-8">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="premium-glossary-toggle"
        className="flex w-full items-center justify-between gap-2 text-left">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-white/70"><BookOpen className="h-4 w-4" /> <T>Panchangam Glossary</T></h3>
        <span className="inline-flex items-center gap-1 text-xs text-amber-300/80">{open ? "Hide" : "Show"}<ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
        <div className="mt-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="premium-glossary-search" placeholder="Search terms…"
            className="mb-3 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-amber-400/50 focus:outline-none" />
          <dl className="divide-y divide-white/10">
            {list.map((g, i) => (
              <div key={i} className="py-3">
                <dt className="flex items-baseline gap-2"><span className="text-sm font-semibold text-white">{g.transliteration}</span>{g.devanagari && <span className="text-sm text-amber-200/80">{g.devanagari}</span>}</dt>
                <dd className="mt-0.5 text-xs leading-relaxed text-white/70">{g.meaning}</dd>
              </div>
            ))}
            {list.length === 0 && <p className="py-4 text-sm text-white/40"><T>No terms match “</T>{q}”.</p>}
          </dl>
        </div>
      )}
    </section>
  );
}

function BirthPrompt({ onAdd }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center" data-testid="birth-prompt">
      <p className="text-white/80"><T>Add your birth details to unlock this section.</T></p>
      <button onClick={onAdd} className="mt-4 rounded-full bg-amber-400 px-5 py-2 text-sm font-semibold text-black"><T>Add birth details</T></button>
    </div>
  );
}

export default function PanchangamPremiumApp() {
  // Tab lives in the URL (?tab=) so each tab is a real navigation: moving forward
  // opens at the top, and browser Back returns to the previous tab at its old
  // scroll position (handled globally by <ScrollToTop/>).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "daily";
  const setTab = (k) => setSearchParams((prev) => { const p = new URLSearchParams(prev); if (k === "daily") p.delete("tab"); else p.set("tab", k); return p; }, { replace: tab !== "daily" });
  // Remember scroll position per tab and restore it when navigating back in.
  useEffect(() => {
    const onScroll = () => { try { sessionStorage.setItem(`plux_scroll_${tab}`, String(window.scrollY)); } catch { /* ignore */ } };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [tab]);
  const { lang, setPageLang } = useLang();
  const { panchang_tamil = {} } = useSettings() || {};
  const pt = useMemo(() => makePanchangT(lang, panchang_tamil), [lang, panchang_tamil]);
  const tr = (k) => (PLUX_I18N[lang] || PLUX_I18N.en)[k] || PLUX_I18N.en[k] || k;
  const [loc, setLoc] = useState(initLoc);
  const [locating, setLocating] = useState(false);
  const [birth, setBirth] = useState(undefined); // undefined=loading, null=none
  const [locOpen, setLocOpen] = useState(false);
  const [whenOpen, setWhenOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  // Daily "showing for" override. null = live now. {date:'YYYY-MM-DD', time:'HH:MM'}
  const [dailyWhen, setDailyWhen] = useState(null);
  // "Today" in the picked location's timezone (not UTC) — keeps chip/greeting/engine aligned.
  const curISO = isoInTz(loc.tz);
  const [birthOpen, setBirthOpen] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [cache, setCache] = useState({});
  const [busy, setBusy] = useState(false);
  // Restore the saved scroll position once a tab's content has loaded.
  useEffect(() => {
    let y = 0; try { y = parseInt(sessionStorage.getItem(`plux_scroll_${tab}`) || "0", 10) || 0; } catch { y = 0; }
    if (!y) return;
    const t1 = setTimeout(() => window.scrollTo(0, y), 220);
    const t2 = setTimeout(() => window.scrollTo(0, y), 650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [tab, busy]);
  const [err, setErr] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [familyPlan, setFamilyPlan] = useState(false);
  const [manageProfile, setManageProfile] = useState(null);
  const [allFests, setAllFests] = useState([]);
  const [birthGate, setBirthGate] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // profile pending delete confirmation

  // Festival calendar (editorial content, not the Jyotish engine) — fetched once.
  useEffect(() => { fetchCalendar().then((d) => setAllFests(d?.festivals || [])).catch(() => {}); }, []);

  // Birth-details gate for new users: once birth resolves to "none" and the
  // user hasn't seen/dismissed it before, invite them to personalise.
  useEffect(() => {
    if (birth === null) {
      try { if (!localStorage.getItem("dharmartha_birth_gate_seen")) setBirthGate(true); } catch { /* ignore */ }
    }
  }, [birth]);
  const dismissBirthGate = () => { try { localStorage.setItem("dharmartha_birth_gate_seen", "1"); } catch { /* ignore */ } setBirthGate(false); };

  // Festivals split into today / upcoming for the shown Daily date.
  const festForShown = (() => {
    const iso = dailyWhen?.date || curISO;
    const today = allFests.filter((f) => f.date === iso);
    const upcoming = allFests.filter((f) => f.date > iso).slice(0, 6);
    return { today, upcoming };
  })();

  useEffect(() => { premiumStatus().then((s) => setFamilyPlan(!!(s?.family_plan || s?.plan === "family"))).catch(() => {}); }, []);

  // Close every sheet/modal whenever the tab changes so nothing "sticks" across
  // the premium app's tab navigation.
  useEffect(() => { setLocOpen(false); setWhenOpen(false); setBirthOpen(false); setRemindOpen(false); setEditProfile(null); setManageProfile(null); setMoreOpen(false); setTranslateOpen(false); setConfirmDel(null); }, [tab]);

  // Back-button behavior: the first Back press closes any open sheet/modal; if a
  // non-Today tab is active it returns to Today (setTab replaces history so Back
  // from any tab lands on Today); only Today exits the app.
  const closeSheets = () => { setLocOpen(false); setWhenOpen(false); setRemindOpen(false); setBirthOpen(false); setMoreOpen(false); setTranslateOpen(false); setBirthGate(false); setEditProfile(null); setManageProfile(null); setConfirmDel(null); };
  const sheetsOpen = locOpen || whenOpen || remindOpen || birthOpen || moreOpen || translateOpen || birthGate || !!editProfile || !!manageProfile || !!confirmDel;
  const closedByBack = useRef(false);
  useEffect(() => {
    if (!sheetsOpen) return;
    closedByBack.current = false;
    window.history.pushState({ pluxSheet: true }, "");
    const onPop = () => { closedByBack.current = true; closeSheets(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Sheet closed by something other than Back (X / backdrop / tab change):
      // pop the dead pluxSheet entry so the next Back isn't swallowed.
      if (!closedByBack.current && window.history.state?.pluxSheet) window.history.back();
    };
  }, [sheetsOpen]);

  // Live wall-clock in the active location's timezone (display only).
  const [clock, setClock] = useState("");
  useEffect(() => {
    const fmt = () => { try { setClock(new Intl.DateTimeFormat("en-US", { timeZone: loc.tz, hour: "numeric", minute: "2-digit", hour12: true }).format(new Date())); } catch { setClock(""); } };
    fmt(); const id = setInterval(fmt, 30000); return () => clearInterval(id);
  }, [loc.tz]);

  // Open the birth form for a new profile, gating family members behind the Family Plan.
  const openAddProfile = () => {
    if (!familyPlan && profiles.length >= 1) {
      toast("Family Plan needed", { description: "Add up to 5 family members + 1 free Muhurta consultation on the Family Plan (₹499/mo)." });
      window.location.assign("/panchanga-premium#family");
      return;
    }
    if (familyPlan && profiles.length >= 5) {
      toast("Family Plan limit reached", { description: "You can save up to 5 birth profiles on the Family Plan. Delete one to add another." });
      return;
    }
    setEditProfile(null); setBirthOpen(true);
  };

  // Load saved family profiles; seed one from the active birth record if empty.
  useEffect(() => {
    premiumGetProfiles().then((d) => {
      let list = d?.profiles || [];
      let act = d?.active_id || null;
      premiumGetBirth().then((b) => {
        if ((!list || list.length === 0) && b && b.dob) {
          const seed = { id: "me", name: b.name || "", nickname: b.nickname || b.name || "Me", dob: b.dob, tob: b.tob, place: b.place, place_id: b.place_id || "", lat: b.lat, lon: b.lon, tz_offset_min: b.tz_offset_min || 0, tz_id: b.tz_id || b.tz_name || "", tz_name: b.tz_name || b.tz_id || "", consent: true };
          list = [seed]; act = "me";
          premiumSaveProfiles(list, act).catch(() => {});
        }
        setProfiles(list); setActiveId(act);
      }).catch(() => { setProfiles(list); setActiveId(act); });
    }).catch(() => {});
  }, []);

  // Swipe to move between tabs (ignores quick taps & vertical scroll).
  const touch = useRef({ x: 0, y: 0 });
  const goTab = (dir) => {
    const idx = TABS.findIndex((t) => t.k === tab);
    const n = idx + dir;
    if (n >= 0 && n < TABS.length) setTab(TABS[n].k);
  };
  const onTouchStart = (e) => { const t = e.changedTouches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x, dy = t.clientY - touch.current.y;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.7) goTab(dx < 0 ? 1 : -1);
  };

  const dateLabelFor = (which) => {
    try {
      if (which === "birthpan" && birth?.dob) {
        const [y, m, d] = String(birth.dob).split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" });
      }
      if (which === "daily" && dailyWhen?.date) {
        const [y, m, d] = dailyWhen.date.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" });
      }
      return new Intl.DateTimeFormat("en-US", { timeZone: loc.tz, weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date());
    } catch { return undefined; }
  };

  useEffect(() => { premiumGetBirth().then((b) => setBirth(b && b.dob ? b : null)).catch((e) => { if (e?.response?.status === 401 || e?.response?.status === 403) setErr(e.response.status); setBirth(null); }); }, []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      if (tab === "daily") {
        const dailyKey = loc.name + "|" + (dailyWhen ? dailyWhen.date + "T" + (dailyWhen.time || "") : "now");
        if (cache.daily && cache.dailyKey === dailyKey) return;
        setBusy(true);
        const base = dailyWhen
          ? (() => { const [y, m, d] = dailyWhen.date.split("-").map(Number); const [hh, mm] = (dailyWhen.time || "12:00").split(":").map(Number); return { year: y, month: m, day: d, hour: hh, min: mm, minute: mm, sec: 0, lat: loc.lat, lon: loc.lon, tz: loc.tz }; })()
          : atNow(loc);
        const [panchang, chogh] = await Promise.all([premiumPanchang(base), premiumChoghadiya(base)]);
        // Render the daily cards immediately; Tara Bala (needs a birth record) is
        // fetched in the background and merged so it never blocks the main view.
        setCache((c) => ({ ...c, daily: { panchang, chogh, tara: null }, dailyKey }));
        setBusy(false);
        if (birth?.dob) {
          const at = base;
          premiumTara({ ...birthPayload(birth), at_year: at.year, at_month: at.month, at_day: at.day, at_hour: at.hour, at_min: at.min, at_minute: at.min, at_sec: at.sec, at_lat: loc.lat, at_lon: loc.lon, at_tz: loc.tz })
            .then((tara) => setCache((c) => (c.dailyKey === dailyKey && c.daily ? { ...c, daily: { ...c.daily, tara } } : c)))
            .catch(() => {});
        }
        return;
      } else if (tab === "chart" && birth?.dob) {
        if (cache.chart) return; setBusy(true);
        const chart = await premiumChart(birthPayload(birth));
        setCache((c) => ({ ...c, chart }));
      } else if (tab === "dasha" && birth?.dob) {
        if (cache.dasha) return; setBusy(true);
        const dasha = await premiumDasha(birthPayload(birth));
        setCache((c) => ({ ...c, dasha }));
      } else if (tab === "birthpan" && birth?.dob) {
        if (cache.birthpan) return; setBusy(true);
        const bp = birthPayload(birth);
        const [panchang, chogh] = await Promise.all([premiumPanchang(bp), premiumChoghadiya(bp)]);
        setCache((c) => ({ ...c, birthpan: { panchang, chogh } }));
      }
    } catch (e) { setErr(e?.response?.status || "error"); } finally { setBusy(false); }
  }, [tab, loc, birth, cache, dailyWhen]);

  useEffect(() => { if (birth !== undefined) load(); /* eslint-disable-next-line */ }, [tab, loc, birth, dailyWhen]);

  const castPrashna = async () => {
    setBusy(true); setErr(null);
    try { const prashna = await premiumChart(atNow(loc)); setCache((c) => ({ ...c, prashna, prashnaAt: new Date().toLocaleTimeString() })); }
    catch (e) { setErr(e?.response?.status || "error"); } finally { setBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Your browser does not support location."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const lat = +p.coords.latitude.toFixed(5);
        const lon = +p.coords.longitude.toFixed(5);
        let name = "My location", tz_id = deviceTz(), tz_off = 330, tz_name = "";
        try {
          const [rj, tj] = await Promise.all([
            fetch(`${API}/api/geo/reverse?lat=${lat}&lon=${lon}`).then((r) => (r.ok ? r.json() : null)),
            fetch(`${API}/api/geo/timezone?lat=${lat}&lon=${lon}`).then((r) => (r.ok ? r.json() : null)),
          ]);
          if (rj?.name) name = rj.name;
          if (tj?.tz_id) tz_id = tj.tz_id;
          if (typeof tj?.tz === "number") tz_off = tj.tz;
          if (tj?.tz_name) tz_name = tj.tz_name;
        } catch { /* keep device tz */ }
        setLoc({ name, lat, lon, tz: tz_id });
        setSavedLocation({ name, lat, lon, tz: tz_off, tz_id, tz_name });
        setCache((c) => ({ ...c, daily: null }));
        setLocating(false); setLocOpen(false);
      },
      (e) => {
        setLocating(false);
        if (e.code === 1) {
          toast.error("Allow precise location", { description: "Tap the location icon in your browser's address bar (or Site settings) → Allow, then press Detect again for your exact place." });
        } else {
          toast.error("Couldn't get your location", { description: "Turn on GPS / precise location and try again." });
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const saveBirth = async (form) => {
    const saved = { ...form };
    await premiumSaveBirth({ dob: form.dob, tob: form.tob, tob_unknown: !!form.tob_unknown, lat: form.lat, lon: form.lon, tz_offset_min: form.tz_offset_min || 0, tz_id: form.tz_id || form.tz_name || "", tz_name: form.tz_name, place: form.place, place_id: form.place_id || "", name: form.name || "", nickname: form.nickname || "", consent: !!form.consent });
    setBirth({ ...saved, tz_name: form.tz_name });
    const id = form.id || `p-${Date.now()}`;
    const entry = { id, name: form.name || "", nickname: form.nickname || "", dob: form.dob, tob: form.tob, tob_unknown: !!form.tob_unknown, place: form.place, place_id: form.place_id || "", lat: form.lat, lon: form.lon, tz_offset_min: form.tz_offset_min || 0, tz_id: form.tz_id || form.tz_name || "", tz_name: form.tz_name || "", consent: !!form.consent };
    const next = profiles.some((p) => p.id === id) ? profiles.map((p) => (p.id === id ? entry : p)) : [...profiles, entry];
    setProfiles(next); setActiveId(id);
    premiumSaveProfiles(next, id).catch(() => {});
    setCache({}); setBirthOpen(false); setEditProfile(null);
  };

  const selectProfile = async (p) => {
    setActiveId(p.id);
    setBirth({ name: p.name || "", nickname: p.nickname || "", dob: p.dob, tob: p.tob, place: p.place, place_id: p.place_id || "", lat: p.lat, lon: p.lon, tz_offset_min: p.tz_offset_min || 0, tz_id: p.tz_id || p.tz_name || "", tz_name: p.tz_name || p.tz_id || "" });
    setCache({}); setBusy(true);
    try {
      await premiumSaveBirth({ dob: p.dob, tob: p.tob, lat: p.lat, lon: p.lon, tz_offset_min: p.tz_offset_min || 0, tz_id: p.tz_id || p.tz_name || "", tz_name: p.tz_name, place: p.place, place_id: p.place_id || "", name: p.name || "", nickname: p.nickname || "", consent: true });
      premiumSaveProfiles(profiles, p.id).catch(() => {});
    } catch (e) { /* recompute happens on next load */ } finally { setBusy(false); }
  };

  const deleteProfile = async (id) => {
    const next = profiles.filter((p) => p.id !== id);
    const act = activeId === id ? (next[0]?.id || null) : activeId;
    setProfiles(next); setActiveId(act);
    premiumSaveProfiles(next, act).catch(() => {});
    if (next.length === 0) setBirth(null);
  };

  const navLabel = (() => { if (tab === "prashna") return tr("prashna"); const t = TABS.find((t) => t.k === tab || (t.group && t.group.includes(tab))); return t ? tr(t.labelKey) : "Premium"; })();

  return (
    <div className="premium-lux plux-app min-h-screen">
      {/* Row 1 — sticky crown + Premium / location · bell · theme */}
      <header className="plux-h1 sticky top-0 z-30 flex items-center">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3 px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/" data-testid="back-to-dharmartha" aria-label="Back to Dharmartha" title="Back to Dharmartha" className="plux-chip shrink-0">
              <ChevronLeft className="h-4 w-4" /> <T>Dharmartha</T>
            </Link>
            <span className="truncate text-sm font-medium text-white/55" data-testid="premium-page-title">· {navLabel}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={() => setTranslateOpen(true)} data-testid="premium-translate-btn" aria-label={tr("translate")} title={tr("translate")}
              className="plux-chip !px-2.5">
              <Languages className="h-4 w-4 plux-crown" /> <span className="hidden text-xs sm:inline">{LANG_PILLS.find((l) => l.code === lang)?.label || "English"}</span>
            </button>
            <button onClick={() => setMoreOpen(true)} data-testid="premium-more-btn" aria-label={tr("more")} className="plux-iconbtn">
              <MoreHorizontal className="h-[18px] w-[18px]" />
            </button>
            <button onClick={() => setRemindOpen(true)} data-testid="premium-bell" aria-label="Reminders" className="plux-iconbtn">
              <Bell className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </header>

      {/* Row 2 — family avatar chips */}
      <div className="plux-h2" data-testid="profile-switcher">
        <div className="mx-auto flex max-w-3xl items-center gap-3 overflow-x-auto px-4 py-2.5 no-scrollbar">
          {profiles.map((p) => {
            const on = activeId === p.id;
            return (
              <div key={p.id} className="plux-ava group relative" data-on={on} onClick={() => selectProfile(p)} data-testid={`profile-${p.id}`}>
                <span className="plux-ava-ring">{initialOf(p.nickname || p.name)}</span>
                <span className="plux-ava-name">{p.nickname || p.name || "Profile"}</span>
                <button onClick={(e) => { e.stopPropagation(); setManageProfile(p); }} data-testid={`profile-manage-${p.id}`}
                  className="absolute -right-0.5 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-black shadow ring-2 ring-black/25" title="Edit or delete">
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          <button onClick={openAddProfile} data-testid="profile-add" className="plux-ava-add shrink-0">
            <Plus className="h-4 w-4" /> {profiles.length ? "Add" : "Birth"}
          </button>
        </div>
      </div>

      {/* Row 3 — unified date + location bar (Daily tab controls the shown moment) */}
      {tab === "daily" && (
        <div className="plux-h3" data-testid="daily-date-strip">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-2 px-4 py-3">
            <div className="flex items-center gap-1.5">
              <button className="plux-navbtn" data-testid="date-prev" aria-label="Previous day"
                onClick={() => { const cur = dailyWhen?.date || curISO; setDailyWhen({ date: shiftISO(cur, -1), time: dailyWhen?.time || new Date().toTimeString().slice(0, 5) }); }}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button className="plux-datebtn" data-testid="date-open" onClick={() => setWhenOpen(true)}>
                <CalendarDays className="h-4 w-4 plux-crown" />
                {fmtStrip(dailyWhen?.date || curISO)}
              </button>
              <button className="plux-navbtn" data-testid="date-next" aria-label="Next day"
                onClick={() => { const cur = dailyWhen?.date || curISO; setDailyWhen({ date: shiftISO(cur, 1), time: dailyWhen?.time || new Date().toTimeString().slice(0, 5) }); }}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            {dailyWhen && (
              <button className="plux-now" data-testid="date-now" onClick={() => setDailyWhen(null)}>
                <RotateCcw className="h-3.5 w-3.5" /> <T>Now</T>
              </button>
            )}
            <span className="hidden h-5 w-px bg-white/12 sm:block" aria-hidden />
            <button onClick={() => setLocOpen(true)} data-testid="location-chip" className="plux-chip !py-1">
              <MapPin className="h-3.5 w-3.5 plux-crown" /> <span className="max-w-[12rem] truncate">{shortLoc(loc.name)}</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 py-6 pb-[calc(148px+env(safe-area-inset-bottom))] md:px-6">
        {err === 401 || err === 403 ? (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-center" data-testid="premium-paywall">
            <p className="text-white/90"><T>Panchangam Premium is required to view this dashboard.</T></p>
            <Link to="/panchanga-premium" className="mt-4 inline-block rounded-full bg-amber-400 px-5 py-2 text-sm font-semibold text-black"><T>View Premium</T></Link>
          </div>
        ) : (
          <SectionErrorBoundary key={tab}>
          <motion.div key={tab} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22, ease: "easeOut" }}>
            {tab === "daily" && (
              <>
                {busy && !cache.daily ? <OmLoader msg={<T>Calculating cosmic time…</T>} /> : cache.daily ? <><PremiumPanchang pt={pt} panchang={cache.daily.panchang} choghadiya={cache.daily.chogh} tara={cache.daily.tara} profile={birth} place={loc.name} dateLabel={dateLabelFor("daily")} festivals={festForShown} onAddBirth={() => setBirthOpen(true)} selectedSec={dailyWhen ? timeToSec(dailyWhen.time) : null} /><PremiumGlossary /></> : err ? <Note><T>Could not reach the Panchangam engine. Try again shortly.</T></Note> : <OmLoader msg={<T>Calculating cosmic time…</T>} />}
              </>
            )}

            {(tab === "chart" || tab === "dasha" || tab === "birthpan") && (
              <>
                <Segmented value={tab} onChange={setTab} options={[{ k: "chart", label: tr("kundali") }, { k: "dasha", label: tr("dasha") }, { k: "birthpan", label: tr("birthPanchangam") }]} />
                {tab === "chart" && (birth === null ? <BirthPrompt onAdd={() => setBirthOpen(true)} /> : busy && !cache.chart ? <OmLoader msg={<T>Casting your Kundali…</T>} /> : cache.chart ? <ChartBlock chart={cache.chart} pt={pt} /> : <OmLoader msg={<T>Casting your Kundali…</T>} />)}
                {tab === "dasha" && (birth === null ? <BirthPrompt onAdd={() => setBirthOpen(true)} /> : busy && !cache.dasha ? <OmLoader msg={<T>Mapping your planetary periods…</T>} /> : cache.dasha ? <AdvancedDasha dasha={cache.dasha} pt={pt} /> : <OmLoader msg={<T>Mapping your planetary periods…</T>} />)}
                {tab === "birthpan" && (birth === null ? <BirthPrompt onAdd={() => setBirthOpen(true)} /> : busy && !cache.birthpan ? <OmLoader msg={<T>Reading your birth sky…</T>} /> : cache.birthpan ? <PremiumPanchang pt={pt} mode="birth" panchang={cache.birthpan.panchang} choghadiya={cache.birthpan.chogh} place={`${birth.place || "Birth place"} · born ${birth.tob || ""}`} dateLabel={dateLabelFor("birthpan")} selectedSec={timeToSec(birth?.tob)} /> : <OmLoader msg={<T>Reading your birth sky…</T>} />)}
              </>
            )}

            {tab === "japa" && <JapaMini />}

            {tab === "prashna" && (
              <div data-testid="prashna-tab">
                <h2 className="mb-3 flex items-center gap-2 font-serif-latin text-xl"><Compass className="h-5 w-5 text-amber-400" /> <T>Live Prashna</T></h2>
                <div className="mb-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="prashna-location-section">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-300/70"><T>Location</T></p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => setLocOpen(true)} data-testid="prashna-location" className="plux-chip">
                      <MapPin className="h-4 w-4 plux-crown" /> <span className="max-w-[12rem] truncate">{shortLoc(loc.name)}</span> <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </button>
                    <button onClick={useMyLocation} disabled={locating} data-testid="prashna-detect"
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 px-3 py-1.5 text-sm text-amber-200 disabled:opacity-60">
                      {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} <T>Detect my location</T>
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-white/45"><T>For your exact place, allow precise location when your browser asks.</T></p>
                </div>
                <div>
                  <button onClick={castPrashna} disabled={busy} className="rounded-full bg-amber-400 px-6 py-3 text-sm font-semibold text-black disabled:opacity-60">{busy ? "Casting…" : "Cast Prashna now"}</button>
                </div>
                {err && !cache.prashna && <div className="mt-4"><Note><T>Could not cast. Please try again.</T></Note></div>}
                {cache.prashna && <div className="mt-5"><ChartBlock chart={cache.prashna} pt={pt} caption={`Snapshot cast at ${cache.prashnaAt}`} /></div>}
              </div>
            )}

            {tab === "muhurta" && (
              <div data-testid="muhurta-tab" className="rounded-[1.75rem] border border-amber-400/15 bg-white/[0.03] p-8 text-center">
                <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 text-amber-300"><CalendarClock className="h-7 w-7" /></span>
                <h2 className="font-serif-latin text-2xl text-white">{tr("muhurta")}</h2>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/70">{tr("comingSoon")}</p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/60"><T>A purpose-based Muhurta finder — pick an activity (travel, griha pravesh, naming) and we'll surface the most auspicious windows for your place, straight from the Dharmartha engine.</T></p>
              </div>
            )}
          </motion.div>
          </SectionErrorBoundary>
        )}
      </div>

      {/* Bottom nav — fixed, theme-aware, stays above scrolling content */}
      <nav className="plux-nav fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)]" data-testid="premium-bottom-nav">
        <div className="mx-auto flex max-w-3xl">
          {TABS.map(({ k, labelKey, Icon, group, iconClass }) => {
            const on = tab === k || (group && group.includes(tab));
            return (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`plux-navitem relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${on ? "is-on" : ""}`}>
              <Icon className={`h-5 w-5 ${iconClass || ""}`} />
              <span>{tr(labelKey)}</span>
              <span className="plux-navdot mt-0.5 h-1 w-1 rounded-full" />
            </button>
            );
          })}
        </div>
      </nav>

      {/* Location sheet */}
      {locOpen && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4" onClick={() => setLocOpen(false)}>
          <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/15 bg-[#121829] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h3 className="font-serif-latin text-lg"><T>Choose location</T></h3><button onClick={() => setLocOpen(false)}><X className="h-5 w-5 text-white/60" /></button></div>
            <button onClick={useMyLocation} disabled={locating} data-testid="use-my-location" className="mb-4 flex w-full items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm disabled:opacity-60">
              {locating ? <Loader2 className="h-4 w-4 animate-spin text-amber-300" /> : <Navigation className="h-4 w-4 text-amber-300" />}
              {locating ? "Detecting your location…" : "Use my location"}
            </button>
            <p className="mb-2 text-xs text-white/50"><T>or search a city</T></p>
            <PremiumPlaceInput testid="premium-location-search" biasLat={loc?.lat} biasLon={loc?.lon} onSelect={(p) => {
              setLoc({ name: p.name, lat: p.lat, lon: p.lon, tz: p.tz_id || deviceTz() });
              setSavedLocation({ name: p.name, lat: p.lat, lon: p.lon, tz: p.tz_offset_min ?? 330, tz_id: p.tz_id || "", tz_name: p.tz_name || "" });
              setCache((c) => ({ ...c, daily: null })); setLocOpen(false);
            }} />
          </div>
        </div>
      )}

      {/* Profile manage popup — tap a profile's pencil to edit/delete (touch-friendly) */}
      {manageProfile && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4" onClick={() => setManageProfile(null)}>
          <div className="w-full max-w-xs rounded-3xl border border-white/15 bg-[#121829] p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="profile-manage-sheet">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 font-serif-latin text-xl text-amber-300">{initialOf(manageProfile.nickname || manageProfile.name)}</div>
            <p className="mt-3 font-serif-latin text-lg text-white">{manageProfile.nickname || manageProfile.name || "Profile"}</p>
            {manageProfile.dob && <p className="mt-0.5 text-xs text-white/50">{prettyDob(manageProfile.dob)}{manageProfile.place ? ` · ${shortLoc(manageProfile.place)}` : ""}</p>}
            <div className="mt-5 space-y-2.5">
              <button onClick={() => { selectProfile(manageProfile); setManageProfile(null); }} data-testid="profile-manage-select" className="w-full rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-black"><T>Use this profile</T></button>
              <button onClick={() => { setEditProfile(manageProfile); setBirthOpen(true); setManageProfile(null); }} data-testid="profile-manage-edit" className="w-full rounded-full border border-white/20 py-2.5 text-sm text-white/85"><T>Edit birth details</T></button>
              {profiles.length > 1 && <button onClick={() => { setConfirmDel(manageProfile); setManageProfile(null); }} data-testid="profile-manage-delete" className="w-full rounded-full border border-rose-500/40 py-2.5 text-sm text-rose-300"><T>Delete profile</T></button>}
            </div>
          </div>
        </div>
      )}

      {/* Confirm before deleting a profile */}
      {confirmDel && (
        <div className="plux-modal fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirmDel(null)} data-testid="confirm-delete">
          <div className="w-full max-w-xs rounded-3xl border border-rose-500/30 bg-[#121829] p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/15 text-rose-300"><Trash2 className="h-6 w-6" /></div>
            <h3 className="mt-3 font-serif-latin text-lg text-white"><T>Delete this profile?</T></h3>
            <p className="mt-1.5 text-sm text-white/60">{confirmDel.nickname || confirmDel.name || "This profile"} <T>and its birth details will be removed. This can't be undone.</T></p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirmDel(null)} data-testid="confirm-delete-cancel" className="flex-1 rounded-full border border-white/20 py-2.5 text-sm text-white/85"><T>Cancel</T></button>
              <button onClick={() => { deleteProfile(confirmDel.id); setConfirmDel(null); }} data-testid="confirm-delete-confirm" className="flex-1 rounded-full bg-rose-500 py-2.5 text-sm font-semibold text-white"><T>Delete</T></button>
            </div>
          </div>
        </div>
      )}

      {/* Birth details sheet */}
      {birthOpen && <BirthForm initial={editProfile} loc={loc} onClose={() => { setBirthOpen(false); setEditProfile(null); }} onSave={saveBirth} />}

      {/* Birth-details welcome gate — invites new users to personalise (once). */}
      {birthGate && birth === null && !birthOpen && (
        <div className="plux-modal fixed inset-0 z-[9997] flex items-center justify-center bg-black/60 p-4" onClick={dismissBirthGate} data-testid="birth-gate">
          <div className="w-full max-w-sm rounded-3xl border border-amber-400/20 bg-[#121829] p-7 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 text-amber-300"><Sparkles className="h-7 w-7" /></div>
            <h3 className="font-serif-latin text-xl text-white"><T>Personalise your Panchangam</T></h3>
            <p className="mt-2 text-sm leading-relaxed text-white/65"><T>Add your birth details to unlock your</T> <span className="text-amber-200"><T>Tara Bala</T></span>, <span className="text-amber-200"><T>Chandrabala</T></span><T>, Kundali and Dasha — tuned to you every day.</T></p>
            <div className="mt-6 space-y-2.5">
              <button onClick={() => { dismissBirthGate(); setBirthOpen(true); }} data-testid="birth-gate-add" className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-3 text-sm font-semibold text-black"><UserPlus className="h-4 w-4" /> <T>Add birth details</T></button>
              <button onClick={dismissBirthGate} data-testid="birth-gate-skip" className="w-full rounded-full border border-white/15 py-2.5 text-sm text-white/70"><T>Maybe later</T></button>
            </div>
          </div>
        </div>
      )}

      {/* Date & time sheet (Daily tab) */}
      {whenOpen && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4" onClick={() => setWhenOpen(false)}>
          <div className="plux-sheet max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="when-sheet">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-serif-latin text-lg"><T>Choose date & time</T></h3><button onClick={() => setWhenOpen(false)}><X className="h-5 w-5 opacity-60" /></button></div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide opacity-60"><T>Date</T></label>
            <input type="date" data-testid="when-date" className="plux-field" value={dailyWhen?.date || curISO} max="2099-12-31"
              onChange={(e) => e.target.value && setDailyWhen({ date: e.target.value, time: dailyWhen?.time || new Date().toTimeString().slice(0, 5) })} />
            <label className="mb-1.5 mt-4 block text-xs font-semibold uppercase tracking-wide opacity-60"><T>Time</T></label>
            <input type="time" data-testid="when-time" className="plux-field" value={dailyWhen?.time || new Date().toTimeString().slice(0, 5)}
              onChange={(e) => e.target.value && setDailyWhen({ date: dailyWhen?.date || curISO, time: e.target.value })} />
            <div className="mt-5 flex gap-3">
              <button onClick={() => { setDailyWhen(null); setWhenOpen(false); }} data-testid="when-now" className="flex-1 rounded-full border border-amber-400/40 py-2.5 text-sm font-semibold text-amber-300"><T>Use now</T></button>
              <button onClick={() => setWhenOpen(false)} data-testid="when-apply" className="flex-1 rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-black"><T>Apply</T></button>
            </div>
          </div>
        </div>
      )}

      {/* Reminders sheet (bell) — placeholder, no data/API touched */}
      {remindOpen && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4" onClick={() => setRemindOpen(false)}>
          <div className="plux-sheet w-full max-w-lg rounded-3xl p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="reminders-sheet">
            <div className="mb-3 flex items-center justify-between text-left"><h3 className="font-serif-latin text-lg"><T>Reminders</T></h3><button onClick={() => setRemindOpen(false)}><X className="h-5 w-5 opacity-60" /></button></div>
            <Bell className="mx-auto h-8 w-8 plux-crown" strokeWidth={1.5} />
            <p className="mt-3 text-sm opacity-70"><T>Daily Panchangam & muhurta reminders are coming to Premium soon.</T></p>
          </div>
        </div>
      )}

      {/* More sheet — settings, Prashna, glossary, account (bottom sheet) */}
      {moreOpen && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-end justify-center bg-black/60" onClick={() => setMoreOpen(false)}>
          <div className="plux-sheet w-full max-w-lg rounded-t-3xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()} data-testid="more-sheet">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-serif-latin text-lg">{tr("more")}</h3><button onClick={() => setMoreOpen(false)} data-testid="more-sheet-close"><X className="h-5 w-5 opacity-60" /></button></div>
            <MoreMenu />
          </div>
        </div>
      )}

      {/* Translate / language sheet */}
      {translateOpen && (
        <div className="plux-modal fixed inset-0 z-[9998] flex items-end justify-center bg-black/60" onClick={() => setTranslateOpen(false)}>
          <div className="plux-sheet w-full max-w-lg rounded-t-3xl p-6 pb-[calc(24px+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()} data-testid="translate-sheet">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-serif-latin text-lg">{tr("language")}</h3><button onClick={() => setTranslateOpen(false)} data-testid="translate-sheet-close"><X className="h-5 w-5 opacity-60" /></button></div>
            <div className="space-y-2">
              {LANG_PILLS.map((l) => (
                <button key={l.code} onClick={() => { setPageLang(l.code); setTranslateOpen(false); }} data-testid={`lang-${l.code}`}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm transition-colors ${lang === l.code ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 bg-white/[0.03] text-white/90 hover:border-white/25"}`}>
                  <span>{l.label}</span>{lang === l.code && <span className="text-amber-300">✓</span>}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/45"><T>This changes the language for the Panchangam app only — the rest of the site keeps your site-wide language. Rashi, nakshatra & planet names translate where available.</T></p>
          </div>
        </div>
      )}
    </div>
  );
}

const JAPA_MANTRAS = [
  { id: "shiva", label: "Oṁ Namaḥ Śivāya", deva: "ॐ नमः शिवाय", iast: "Oṁ Namaḥ Śivāya", meaning: "Salutations to Śiva — the auspicious one." },
  { id: "ganapati", label: "Oṁ Gaṁ Gaṇapataye Namaḥ", deva: "ॐ गं गणपतये नमः", iast: "Oṁ Gaṁ Gaṇapataye Namaḥ", meaning: "Salutations to Gaṇapati, remover of obstacles." },
  { id: "rama", label: "Śrī Rāma Jaya Rāma", deva: "श्री राम जय राम", iast: "Śrī Rāma Jaya Rāma", meaning: "Glory to Śrī Rāma." },
];

function JapaMini() {
  const [mantras, setMantras] = useState(JAPA_MANTRAS);
  const [sel, setSel] = useState(JAPA_MANTRAS[1]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [total, setTotal] = useState(0);
  const malas = Math.floor(total / 108);
  const filled = total === 0 ? 0 : ((total - 1) % 108) + 1;

  const tap = () => setTotal((t) => t + 1);
  const addMantra = () => {
    const t = draft.trim();
    if (!t) { setAdding(false); return; }
    const m = { id: `custom-${Date.now()}`, label: t, deva: t, iast: "" };
    setMantras((arr) => [...arr, m]); setSel(m); setDraft(""); setAdding(false);
  };

  return (
    <div data-testid="japa-tab" className="text-center">
      <h2 className="font-serif-latin text-2xl text-white"><T>Japa Sadhana</T></h2>
      <p className="mt-1 text-sm text-white/55">{malas} <T>malas completed · tap the circle to count</T></p>

      <div className="mt-5 -mx-1 flex gap-2 overflow-x-auto no-scrollbar px-1 pb-1">
        {mantras.map((m) => (
          <button key={m.id} data-testid={`japa-mantra-${m.id}`} onClick={() => { setSel(m); setTotal(0); }}
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${sel.id === m.id ? "border-amber-400/70 bg-amber-400/10 text-amber-200" : "border-white/15 text-white/70 hover:border-white/30"}`}>
            {sel.id === m.id && <span className="text-amber-300">✓</span>} {m.label}
          </button>
        ))}
        <button onClick={() => setAdding(true)} data-testid="japa-add-mantra"
          className="shrink-0 whitespace-nowrap rounded-full border border-dashed border-white/25 px-3.5 py-1.5 text-xs text-white/60 hover:border-amber-400/50 hover:text-amber-200">
          <T>+ Add your mantra</T>
        </button>
      </div>

      {adding && (
        <div className="mx-auto mt-3 flex max-w-sm items-center gap-2">
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMantra()}
            data-testid="japa-custom-input" placeholder="Type your mantra…"
            className="flex-1 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40" />
          <button onClick={addMantra} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-black"><T>Add</T></button>
        </div>
      )}

      <p className="mt-6 font-deva text-3xl leading-relaxed text-amber-300 deva-lh">{sel.deva}</p>
      {sel.iast && <p className="mt-1 text-sm italic text-white/60">{sel.iast}</p>}
      {sel.meaning && <p className="mx-auto mt-1 max-w-sm text-sm text-white/50">{sel.meaning}</p>}

      <div className="mx-auto">
        <JapaMala filled={filled} malaNumber={malas} total={total} onCount={tap} />
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <button onClick={() => setTotal(0)} data-testid="japa-reset"
          className="rounded-full border border-white/15 px-5 py-2 text-sm text-white/70 hover:border-white/30"><T>Reset</T></button>
        <Link to={`/japa?m=${encodeURIComponent("p-" + (sel.id || sel.deva))}&deva=${encodeURIComponent(sel.deva || "")}&translit=${encodeURIComponent(sel.iast || "")}&meaning=${encodeURIComponent(sel.meaning || "")}`} data-testid="japa-full-page"
          className="rounded-full border border-white/15 px-5 py-2 text-sm text-white/70 hover:border-white/30"><T>Full Japa page</T></Link>
      </div>
    </div>
  );
}

function BirthForm({ onClose, onSave, initial, loc }) {
  const [f, setF] = useState({
    id: initial?.id || undefined,
    name: initial?.name || "", nickname: initial?.nickname || "", dob: initial?.dob || "", tob: initial?.tob || "", place: initial?.place || "", place_id: initial?.place_id || "",
    lat: initial?.lat ?? null, lon: initial?.lon ?? null,
    tz_offset_min: initial?.tz_offset_min || 0, tz_id: initial?.tz_id || initial?.tz_name || "", tz_name: initial?.tz_name || initial?.tz_id || "",
    consent: !!initial?.dob,
  });
  const [saving, setSaving] = useState(false);
  const [tobUnknown, setTobUnknown] = useState(!!initial?.tob_unknown);
  const ok = f.name.trim() && f.dob && (f.tob || tobUnknown) && f.lat != null && f.consent;
  const inp = "w-full rounded-lg border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-amber-300/60 focus:outline-none";
  return (
    <div className="plux-modal fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/15 bg-[#121829] p-6" onClick={(e) => e.stopPropagation()} data-testid="birth-form">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-serif-latin text-lg">{initial?.id ? "Edit birth details" : "Add birth details"}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-white/50"><T>Full name</T></label>
              <input type="text" data-testid="birth-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Arun Kumar" className={inp} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-white/50"><T>Nickname</T> <span className="normal-case text-white/35"><T>(on chip)</T></span></label>
              <input type="text" data-testid="birth-nickname" value={f.nickname} onChange={(e) => setF({ ...f, nickname: e.target.value })} placeholder="e.g. Arun, Amma" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-white/50"><T>Date of birth</T></label>
              <input type="date" data-testid="birth-dob" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} className={inp} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-white/50"><T>Time of birth</T></label>
              <input type="time" data-testid="birth-tob" value={f.tob} disabled={tobUnknown} onChange={(e) => setF({ ...f, tob: e.target.value })} className={`${inp} disabled:opacity-40`} />
            </div>
          </div>
          <div>
            <label className="flex items-start gap-2.5 text-xs leading-relaxed text-white/70">
              <input type="checkbox" data-testid="birth-tob-unknown" checked={tobUnknown} onChange={(e) => { setTobUnknown(e.target.checked); if (e.target.checked) setF({ ...f, tob: "" }); }} className="mt-0.5 h-4 w-4 accent-amber-400" />
              <span><T>I don't know my birth time</T></span>
            </label>
            {tobUnknown && (
              <p className="mt-1.5 rounded-lg border border-amber-400/25 bg-amber-400/5 px-3 py-2 text-[11px] leading-relaxed text-amber-200/80" data-testid="birth-tob-note">
                <T>We'll use noon (12:00 PM) as a placeholder. The Ascendant (Lagna), house positions, divisional charts, Hora and exact Dasha timings will be less accurate; Tithi, Nakshatra, Rashi and daily Panchang stay reliable.</T>
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-wide text-white/50"><T>Birth place</T></label>
            <PremiumPlaceInput testid="birth-place" value={f.place} biasLat={f.lat ?? loc?.lat} biasLon={f.lon ?? loc?.lon} onSelect={(p) => setF({ ...f, place: p.name, place_id: p.place_id || "", lat: p.lat, lon: p.lon, tz_offset_min: p.tz_offset_min || 0, tz_id: p.tz_id || "", tz_name: p.tz_id || "" })} />
          </div>
          <label className="flex items-start gap-2.5 text-xs leading-relaxed text-white/60">
            <input type="checkbox" data-testid="birth-consent" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-400" />
            <span><T>I consent to Dharmartha securely storing these birth details to compute the personalised Panchangam (DPDP). I can delete them anytime.</T></span>
          </label>
          <button disabled={!ok || saving} onClick={async () => { setSaving(true); try { await onSave({ ...f, tob: tobUnknown ? "12:00" : f.tob, tob_unknown: tobUnknown }); } finally { setSaving(false); } }}
            data-testid="birth-save" className="w-full rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-black transition-opacity disabled:opacity-50">{saving ? "Saving…" : "Save & calculate"}</button>
        </div>
      </div>
    </div>
  );
}
